import { useCallback, useMemo } from 'react';

import { pseudoUnique } from '@cast/utils';

import { FooterRow } from '../_components/FooterRow';
import { HeaderRow } from '../_components/HeaderRow';
import { CellSkeleton } from '../_components/skeletons/CellSkeleton';
import { RowSkeleton } from '../_components/skeletons/RowSkeleton';
import { Sorter as DefaultSorter } from '../_components/Sorter';
import { makeTreeUiUtils } from '../_utils';
import {
  InfoIcon as DefaultInfoIcon,
  ResizerIcon as DefaultResizerIcon,
  SortIcon as DefaultSortIcon,
} from '../components';
import { BodyRow } from '../components/BodyRow';
import { defaultTreeUiConfig, tableMeasurements } from '../constants';
import { LogPayload, NormalizedTableProps, TableProps, RowKey } from '../types';

const defaultLogger = ({ message, data, logLevel }: LogPayload) => {
  /* eslint-disable no-console */
  let method = console.log;
  switch (logLevel) {
    case 'error':
      method = console.error;
      break;
    case 'warning':
      method = console.warn;
      break;
    case 'info':
      method = console.info;
      break;
  }
  /* eslint-enable no-console */
  method(`[DS-Table] ${message}`, data);
};

export const useTableProps = <T, K extends RowKey>(
  props: TableProps<T, K>
): NormalizedTableProps<T, K> => {
  const {
    rowKey: _rowKey,
    Sorter,
    SortIcon,
    ResizerIcon,
    InfoIcon,
    HeaderRowComponent,
    BodyRowComponent,
    FooterRowComponent,
    loadingRowSkeleton,
    loadingCellSkeleton,
    childRows: _childRows,
  } = props;
  const rowKey = useMemo(() => {
    if (!_rowKey) {
      return (row: any) => row.id;
    } else if (typeof _rowKey === 'function') {
      return _rowKey;
    }
    return (row: any) => row[_rowKey];
  }, [_rowKey]);
  const sizing = props.sizing || 'l';
  const rowHeight = props.rowHeight || tableMeasurements[sizing].rowHeight;
  const treeUiConfig = props.treeTable
    ? props.treeUiConfig || defaultTreeUiConfig
    : undefined;

  const [sysId] = useMemo(
    () => props.testId || String(pseudoUnique()),
    [props.testId]
  );

  if (props.treeTable && !_childRows) {
    throw new Error('Tree table must be provided with "childRows" prop');
  }

  const log = useCallback(
    (payload: LogPayload) => {
      props.log?.(payload);
      defaultLogger(payload);
    },
    [props]
  );

  return {
    ...props,
    rowKey,
    sizing,
    Sorter: Sorter || DefaultSorter,
    SortIcon: SortIcon || DefaultSortIcon,
    ResizerIcon: ResizerIcon || DefaultResizerIcon,
    InfoIcon: InfoIcon || DefaultInfoIcon,
    HeaderRowComponent: HeaderRowComponent || HeaderRow,
    BodyRowComponent: BodyRowComponent || BodyRow,
    FooterRowComponent: FooterRowComponent || FooterRow,
    loadingRowSkeleton: loadingRowSkeleton || <RowSkeleton />,
    loadingCellSkeleton: loadingCellSkeleton || <CellSkeleton />,
    overscan: props.overscan ?? 500,
    rowHeight,
    treeUiConfig,
    treeUiUtils: props.treeTable
      ? props.treeUiUtils || makeTreeUiUtils(treeUiConfig!)
      : undefined,
    childRows: props.childRows,
    testId: props.testId || 'ds-table',
    log,
    loadingMoreText: props.loadingMoreText,
    // Needed for testing driver to support nested tables - to not include child table's body rows
    sysId: props.testId || sysId,
  };
};
